import {
  Anchor,
  Badge,
  Group,
  HoverCard,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  getNodeDetails,
  getTypeIcon,
} from "../../../workflows/CreateCampaign/flow/flowSettings";
import CampaignColumnChildCell from "./CampaignColumnChildCell";
import {
  IconCircleFilled,
  IconListDetails,
  IconUserStar,
} from "@tabler/icons-react";
import EmailModalPreview from "../../../../../components/View/EmailModalPreview";
import MessageModalPreview from "../../../../../components/View/MessageModalPreview";
import { Link } from "react-router-dom";
import SmvModalPreview from "../../../../../components/View/SmvModalPreview";
import VocalMessagePreview from "../../../../../components/View/VocalMessagePreview";
import {
  useConfigStore,
  useModelsStore,
} from "../../../../../stores/ConfigStore";

function getText(type) {
  const lang = useLanguage.getState().language;
  if (type === "linkedin_invitation") {
    return lang.workflows.explorer.all_elements.linkedin_invitation;
  }
  if (type === "linkedin_visit") {
    return lang.workflows.explorer.all_elements.linkedin_visit;
  }
  if (type === "call") {
    return lang.workflows.explorer.all_elements.call;
  }
  if (type === "remove_prospect") {
    return lang.workflows.explorer.all_elements.remove_prospect;
  }
  return "-";
}

function handleModel(step) {
  const lang = useLanguage.getState().language;
  let vocalFiles = useConfigStore
    .getState()
    .files_list.filter(
      (f) => f.file_url.endsWith(".mp3") || f.file_url.endsWith(".wav"),
    );
  const vocalModels = useModelsStore
    .getState()
    .model_linkedin_messages.filter((m) =>
      vocalFiles.find((f) => f.id === m.file_id),
    );
  let isVocal =
    step.action_type === "linkedin_message" &&
    vocalModels.find((m) => m.id === step.model.id);

  if (step.action_type === "email") {
    return (
      <EmailModalPreview
        opener={(show) => (
          <Tooltip
            label={`${lang.global.preview} : ${step?.model?.name} #${step?.model?.id}`}
          >
            <Anchor
              size={"sm"}
              truncate="end"
              flex={"1 1 auto"}
              c={"black"}
              fw={600}
              onClick={show}
            >
              {step?.model?.name || " - "}
            </Anchor>
          </Tooltip>
        )}
        model={step.model}
        noSign
        standalone
      />
    );
  } else if (step.action_type === "smv") {
    return (
      <SmvModalPreview
        opener={(show) => (
          <Tooltip
            label={`${lang.global.preview} : ${step?.model?.name} #${step?.model?.id}`}
          >
            <Anchor
              size={"sm"}
              truncate="end"
              flex={"1 1 auto"}
              c={"black"}
              fw={600}
              onClick={show}
            >
              {step?.model?.name || " - "}
            </Anchor>
          </Tooltip>
        )}
        model={step.model}
      />
    );
  } else if (isVocal) {
    return (
      <VocalMessagePreview
        opener={(show) => (
          <Tooltip
            label={`${lang.global.preview} : ${step?.model?.name} #${step?.model?.id}`}
          >
            <Anchor
              size={"sm"}
              truncate="end"
              flex={"1 1 auto"}
              c={"black"}
              fw={600}
              onClick={show}
            >
              {step?.model?.name || " - "}
            </Anchor>
          </Tooltip>
        )}
        model={step.model}
      />
    );
  } else {
    return (
      <MessageModalPreview
        opener={(show) => (
          <Tooltip
            label={`${lang.global.preview} : ${step?.model?.name} #${step?.model?.id}`}
          >
            <Text
              size={"sm"}
              truncate="end"
              flex={"1 1 auto"}
              c={"black"}
              fw={600}
              onClick={show}
            >
              {step?.model?.name || " - "}
            </Text>
          </Tooltip>
        )}
        model={step.model}
        standalone
        type={step.action_type}
      />
    );
  }
}

const ChildCell = ({ step }) => {
  return (
    <CampaignColumnChildCell>
      {getTypeIcon(step.action_type, false, 15, {
        size: "sm",
        opacity: 0.75,
        variant: "subtle",
        color: getNodeDetails(step.action_type)?.color,
      })}
      {step?.model?.name ? (
        <Group w={"calc(100% - 32px)"} className="hover-source" wrap="nowrap">
          {handleModel(step)}
          {/* <Group>
            <IconEye size={18} />
          </Group> */}
        </Group>
      ) : (
        <Text
          size={"sm"}
          sx={{
            opacity: getText(step?.action_type) === "-" ? 1 : 0.5,
          }}
        >
          {getText(step?.action_type)}
        </Text>
      )}
    </CampaignColumnChildCell>
  );
};

const ListDisplay = ({ lists }) => {
  const lang = useLanguage((s) => s.language);
  return (
    <HoverCard openDelay={200} position="top" offset={2} withArrow>
      <HoverCard.Target>
        <ThemeIcon
          size={"md"}
          variant="light"
          bg={"primary.2"}
          pos={"absolute"}
          right={0}
          top={11}
        >
          <IconListDetails size={20} />
        </ThemeIcon>
      </HoverCard.Target>

      <HoverCard.Dropdown>
        <Stack>
          {lists.length > 0 ? (
            lists?.map((list) => (
              <Group key={list.contact_list_id} gap={"xs"}>
                <IconCircleFilled size={6} color="black" />
                <Anchor
                  size="sm"
                  target="_blank"
                  fw={600}
                  href={`/contact-lists/${list.contact_list_id}`}
                >
                  {list.name} #{list.contact_list_id}
                </Anchor>
              </Group>
            ))
          ) : (
            <Text c={"dimmed"}>{lang.prm.nurturing.no_list}</Text>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

const ParentCell = ({ value, row }) => {
  let step_action_types = [row?.initial?.action_type];

  row.steps.forEach((step) => {
    if (step?.action_type !== "call") {
      step_action_types.push(step?.action_type);
    }
  });

  step_action_types = [...new Set(step_action_types)];

  return (
    <Group w="100%" wrap="nowrap" gap={4}>
      <Stack gap={0}>
        <Group wrap="nowrap" gap={4}>
          {row?.nurturing && (
            <Tooltip label={"Nurturing"}>
              <IconUserStar size={16} />
            </Tooltip>
          )}
          <Tooltip label={`${row?.workflow_name} #${row?.workflow_id}`}>
            <Anchor
              component={Link}
              to={
                row?.is_newsletter
                  ? `/newsletter/edit/${row.id}/workflow/${row.workflow_id}`
                  : `/campaign/edit/${row.id}/workflow/${row.workflow_id}`
              }
              truncate="end"
            >
              {value + ` #${row.workflow_id}`}
            </Anchor>
          </Tooltip>
        </Group>
        <Group wrap="nowrap" gap={4} mt={-4}>
          {step_action_types.map((type) =>
            getTypeIcon(
              type,
              false,
              24,
              {
                size: 16,
                opacity: 1,
                variant: "subtle",
                mt: 4,
                color: getNodeDetails(type)?.color || "secondary",
              },
              false,
              { size: 8 },
              { tipStyles: { top: 0 } },
            ),
          )}
          {row?.is_newsletter && <Badge size="xs" fz={8} mt={4} ml={4} color="blue.4">newsletter</Badge>}
        </Group>
      </Stack>
      <ListDisplay lists={row.contact_lists} />
    </Group>
  );
};

export function CampaignColumnName() {
  return {
    field: "name",
    headerName: "Nom",
    // headerName: lang.statistics.columns.name_model.header_name,
    width: 200,
    sortable: false,
    filterable: false,
    renderCell: ({ row, value }) => {
      if (!row) {
        return;
      }
      if (row.isChild) {
        return (
          <Stack py={"xs"} w={"100%"}>
            {row.steps.map((step, index) => {
              return <ChildCell step={step} key={step.id} />;
            })}
          </Stack>
        );
      }

      return <ParentCell row={row} value={value} />;
    },
  };
}
